import React, { useContext, useState } from "react";
import { HolidayType } from "../../domains/models/holiday";
import dayjs, { Dayjs } from "dayjs";
import { Button, Drawer, Dropdown, Form, Modal, Popconfirm, Tag, Tooltip } from "antd";
import UserLoginContext from "../../context/userLoginContext";
import { Holiday } from "../../domains/models/holiday";
import HolidayCreateModal from "./utils/holiday-create-modal";
import Grid from "../../common/table";
import { IHolidayFormData } from "./types/holidays.types";
import Locale from "../../core/locale/locale";
import { Ellipsis, Pencil, Trash } from "lucide-react";
import HolidayCalendarView from "./utils/holiday-calendar-view";
import HolidayChartsRow from "./utils/holiday-charts-row";
import HolidayPageHeader from "./utils/holiday-page-header";

export interface IHolidayViewProps {
    holidays: Holiday[];
    loading: boolean;
    selectedDate: Dayjs;
    onSelectedDateChange: (value: Dayjs) => void;
    onSubmit: (data: IHolidayFormData) => void;
    deleteHoliday: (id: string) => void;
}

export const HOLIDAY_TYPE_COLORS: Record<string, string> = {
    NATIONAL_HOLIDAY: "blue",
    MANDATORY: "yellow",
    OPTIONAL: "teal",
    RESTRICTED: "red",
    EMERGENCY: "red",
};

export const MONTH_NAMES = Array.from(
    {length: 12},
    (_, index) => dayjs().month(index).format("MMMM")
);

export const WEEKDAY_NAMES = Array.from(
    {length: 7},
    (_, index) => dayjs().day(index).format("dddd")
);

export const holidayTypeLabel = (type: string) => (
    HolidayType[type as keyof typeof HolidayType] || type?.replaceAll("_", " ")
);

export const renderHolidayType = (type: string) => {
    return (
        <span className={`pn-holiday__type pn-holiday__type--${type.toLowerCase()}`}>
            {holidayTypeLabel(type)}
        </span>
    )
}

const HolidayView = (props: IHolidayViewProps) => {
    const [form] = Form.useForm();
    const [open, setOpen] = useState<boolean>(false);
    const [view, setView] = useState<'list' | 'calendar' | 'stats'>('list');
    const [selectedHoliday, setSelectedHoliday] = useState<Holiday | null>(null);
    const {user} = useContext(UserLoginContext);

    const onModalCancel = () => {
        setOpen(false);
        form.resetFields();
    }

    function handleModalOpen(record?: Holiday) {
        setOpen(true);
        if (record) {
            form.setFieldsValue({
                id: record.id,
                name: record.name,
                date: dayjs(record.date),
                type: record.type,
                notify: record.notify ?? true,
            });
            return;
        }
        form.setFieldsValue({
            date: props.selectedDate.isBefore(dayjs(), "day") ? dayjs() : props.selectedDate,
            type: "NATIONAL_HOLIDAY",
            notify: true,
        });
    }

    const columns = [
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            sortable: true,
            filterable: true,
            isDateFormat: false,
            render: (_: string, record: Holiday) => (
                <button
                    className="pn-holiday__link-button"
                    onClick={() => setSelectedHoliday(record)}
                >
                    {record.name}
                </button>
            )
        },
        {
            title: "Date",
            dataIndex: "date",
            key: "date",
            sortable: true,
            render: (_: string, holiday: Holiday) => holiday?.date ? dayjs(holiday?.date).format(Locale.displayDate) : "-"
        },
        {
            title: "Day",
            dataIndex: "day",
            key: "day",
            render: (day: string) => <span className="pn-holiday__day">{day?.toLowerCase()}</span>
        },
        // {
        //     title: "Notify",
        //     dataIndex: "notify",
        //     key: "notify",
        //     render: (notify: boolean) => (
        //         <span className={`pn-holiday__notify-indicator ${notify ? 'yes' : 'no'}`}>
        //             {notify ? <Bell size={13}/> : <BellOff size={13}/>}
        //             {notify ? 'Yes' : 'No'}
        //         </span>
        //     )
        // },
        {
            title: "Type",
            dataIndex: "type",
            key: "type",
            filterable: true,
            datatypeCovert: {
                isConvertable: true,
                holidayDatatype: HolidayType,
            },
            render: (type: string) => renderHolidayType(type)
        },
        ...(user?.isAdmin ? [{
                title: "Action",
                key: "action",
                width:'5%',
                render: (_: unknown, record: Holiday) => {
                    const isPastDate = dayjs(record.date).isBefore(dayjs(), "day");
                    const menuItems = [
                        {
                            label: 'Update',
                            key: 0,
                            icon: <Pencil className={`action-edit ${isPastDate ? 'action-disabled' : ''}`}/>,
                            onClick: () => {
                                if (!isPastDate) {
                                    handleModalOpen(record)
                                } else {
                                    Modal.info(
                                        {
                                            title: 'Cannot Update',
                                            content: 'Past holidays cannot be updated'
                                        }
                                    )
                                }
                            },
                            disabled: isPastDate
                        },
                        {
                            label: 'Delete',
                            key: 1,
                            icon: <Trash
                                className={"action-delete"}
                            />,
                            onClick: () => {
                                Modal.confirm(
                                    {
                                        title: 'Holiday Will be Deleted',
                                        content: 'Are you sure to delete ',
                                        onOk: () => record.id && props.deleteHoliday(record.id)
                                    }
                                )

                            }
                        }
                    ]
                    return (
                        <Dropdown menu={{items: menuItems}} overlayClassName={'pn-table-action-menu'}>
                            <Ellipsis className={'pn-table-actions-icon'}/>
                        </Dropdown>
                    );
                },
            }] : []
        )
    ];

    function getCurrentView() {
        switch (view) {
            case "list":
                return (
                    <div className="pn-holiday__grid">
                        <Grid
                            bordered
                            scrollable
                            columns={columns}
                            dataSource={props.holidays as Holiday[]}
                            pagination={true}
                        />
                    </div>
                )
            case "calendar" :
                return (
                    <HolidayCalendarView
                        holidays={props.holidays}
                        selectedDate={props.selectedDate}
                        setSelectedHoliday={setSelectedHoliday}
                        onSelectedDateChange={props.onSelectedDateChange}
                    />
                );
            case "stats" :
                return (

                    <HolidayChartsRow
                        holidays={props.holidays}
                        loading={false}
                        year={props.selectedDate.year()}
                    />
                );
            default:
                return (
                    <div className="pn-holiday__grid">
                        <Grid
                            bordered
                            scrollable
                            columns={columns}
                            dataSource={props.holidays as []}
                            pagination={true}
                        />
                    </div>
                )
        }
    }

    return (
        <div className="pn-holiday__page">
            {/* Page Header */}
            <HolidayPageHeader
                selectedDate={props.selectedDate}
                onSelectedDateChange={props.onSelectedDateChange}
                view={view}
                setView={setView}
                handleModalOpen={handleModalOpen}
                isAdmin={user?.isAdmin}
            />

            {getCurrentView()}

            <Drawer
                open={Boolean(selectedHoliday)}
                onClose={() => setSelectedHoliday(null)}
                title={selectedHoliday?.name}
                className="pn-holiday__drawer"
                width={420}
            >
                {selectedHoliday && (
                    <div className="pn-holiday__detail">
                        <Tag className={`pn-holiday__type-pill ${HOLIDAY_TYPE_COLORS[selectedHoliday.type]}`}>
                            {holidayTypeLabel(selectedHoliday.type)}
                        </Tag>
                        <div className="pn-holiday__detail-row">
                            <span>Date</span>
                            <strong>{dayjs(selectedHoliday.date).format(Locale.displayDate)}</strong>
                        </div>
                        <div className="pn-holiday__detail-row">
                            <span>Day</span>
                            <strong>{selectedHoliday.day}</strong>
                        </div>
                        <div className="pn-holiday__detail-row">
                            <span>Year</span>
                            <strong>{selectedHoliday.year}</strong>
                        </div>
                        {selectedHoliday.auditInfo && (
                            <div className="pn-holiday__audit">
                                <span>Created by {selectedHoliday.auditInfo.createdBy}</span>
                                <span>{dayjs(selectedHoliday.auditInfo.createdOn).format("DD MMM YYYY, hh:mm A")}</span>
                            </div>
                        )}
                        {user?.isAdmin && !dayjs(selectedHoliday.date).isBefore(dayjs(), "day") && (
                            <Button
                                type="primary"
                                icon={<Pencil/>}
                                onClick={() => {
                                    handleModalOpen(selectedHoliday);
                                    setSelectedHoliday(null);
                                }}
                            >
                                Edit Holiday
                            </Button>
                        )}
                    </div>
                )}
            </Drawer>

            <HolidayCreateModal
                form={form}
                onModalCancel={onModalCancel}
                onSubmit={props.onSubmit}
                open={open}
                setOpen={setOpen}
            />
        </div>
    )
}

export default HolidayView;
